import { toPng } from "html-to-image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GetAccount200ResponsePositionsInner,
  SideResponse,
} from "../../codegen-api";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { formatRoiPercent, signedTone } from "../../utils/unknownNumbers";
import { getTradeEntryPrice, getTradeNetPnl } from "../../utils/trade";
import { useToast } from "../../hooks/toast";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import { formatCurrencyWithSubscript } from "../../utils/format";
import { resolveRoi } from "../../utils/positionRoi";
import { apiNum } from "../../utils/math";
import { getAppDomain } from "config/chains";
import { ICardTheme } from "./style";
import { TRANSPARENT_PLACEHOLDER } from "./helpers";
import { buildPnlTweetText } from "./pnlTweet";
import { BgMode, IPnLCardProps, Orientation } from "./types";

export function usePnLCardModal(props: IPnLCardProps) {
  const { pnlType, pnlData, show, onHidePnLCard } = props;
  const [orientation, setOrientation] = useState<Orientation>("landscape");
  const [bgMode, setBgMode] = useState<BgMode>("chart");
  const [downloading, setDownloading] = useState(false);
  const [copying, setCopying] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);
  const { addToast } = useToast();

  useEffect(() => {
    if (!show) {
      const timer = setTimeout(onHidePnLCard, 400);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [onHidePnLCard, show]);

  // PnL calculation
  const pnlContent = useMemo(() => {
    if (!pnlData) return undefined;

    if (pnlType === "position") {
      // Single source of truth for displayed ROE — same function the positions
      // table and details modal use, so a shared card can never quote a
      // different number than the row it was opened from.
      // Unknown PnL (market unresolved) shares as "-", never "+$0.00 / +0.00%".
      const pnl = apiNum(pnlData.unrealized_pnl);
      const roi =
        pnl !== undefined
          ? resolveRoi(pnl, pnlData as GetAccount200ResponsePositionsInner)
          : undefined;
      return {
        text: formatRoiPercent(roi),
        color: signedTone(roi, {
          up: COLORS.up.one,
          down: COLORS.down.one,
          unknown: TEXT_COLORS.three,
        }),
        dollarText: formatCurrencyWithSubscript(pnl, {
          showPositiveSign: true,
        }),
      };
    }

    // trade
    const netPnl = getTradeNetPnl(pnlData);
    const entryPrice = Number(getTradeEntryPrice(pnlData));
    let roi = 0;
    if (entryPrice && Number(pnlData.amount)) {
      if (pnlData.side === SideResponse.Sell) {
        roi =
          ((Number(pnlData.price) - entryPrice) * Number(pnlData.amount) -
            Number(pnlData.fees)) /
          (entryPrice * Number(pnlData.amount));
      } else {
        roi =
          ((entryPrice - Number(pnlData.price)) * Number(pnlData.amount) -
            Number(pnlData.fees)) /
          (entryPrice * Number(pnlData.amount));
      }
    }
    return {
      text: `${roi >= 0 ? "+" : ""}${(roi * 100).toFixed(2)}%`,
      color: roi >= 0 ? COLORS.up.one : COLORS.down.one,
      dollarText: formatCurrencyWithSubscript(netPnl || 0, {
        showPositiveSign: true,
      }),
    };
  }, [pnlData, pnlType]);

  const cardTheme: ICardTheme = useMemo(
    () => ({
      bgMode,
      orientation,
      isPositive:
        pnlContent?.color === COLORS.up.one ||
        pnlContent?.text?.startsWith("+") ||
        false,
    }),
    [bgMode, orientation, pnlContent]
  );

  // Export helpers
  const captureImage = useCallback(
    async (): Promise<string | null> => {
      if (!exportRef.current) return null;
      return toPng(exportRef.current, {
        quality: 1,
        pixelRatio: 3,
        cacheBust: true,
        includeQueryParams: true,
        imagePlaceholder: TRANSPARENT_PLACEHOLDER,
        filter: (node) => {
          const tag = node.tagName?.toUpperCase();
          return tag !== "VIDEO" && tag !== "SOURCE";
        },
      });
    },
    []
  );

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const dataUrl = await captureImage();
      if (!dataUrl) return;
      const link = document.createElement("a");
      link.download = `${pnlData?.instrument_name ?? "pnl"}.png`;
      link.href = dataUrl;
      link.click();
    } catch {
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Could not export image",
          subheader: "Please try again.",
          status: ToastStatusEnum.ERROR,
        },
        4000
      );
    } finally {
      setDownloading(false);
    }
  }, [addToast, captureImage, pnlData?.instrument_name]);

  const handleCopy = useCallback(async () => {
    setCopying(true);
    try {
      const dataUrl = await captureImage();
      if (!dataUrl) return;
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Copied to clipboard",
          status: ToastStatusEnum.SUCCESS,
        },
        2000
      );
    } catch {
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Could not copy image",
          subheader: "Your browser may not support clipboard image write.",
          status: ToastStatusEnum.ERROR,
        },
        4000
      );
    } finally {
      setCopying(false);
    }
  }, [addToast, captureImage]);

  const handleTwitterShare = useCallback(() => {
    const isLong =
      pnlType === "position"
        ? pnlData?.side === SideResponse.Buy
        : pnlData?.side === SideResponse.Sell;
    const dir = isLong ? "Long" : "Short";
    const text = buildPnlTweetText({
      direction: dir,
      instrumentName: pnlData?.instrument_name,
      roiText: pnlContent?.text,
      pnlText: pnlContent?.dollarText,
      source: pnlData as Parameters<typeof buildPnlTweetText>[0]["source"],
      appDomain: getAppDomain(),
    });
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
      "_blank",
      "noopener,noreferrer"
    );
  }, [pnlContent, pnlData, pnlType]);

  return {
    orientation,
    setOrientation,
    bgMode,
    setBgMode,
    downloading,
    copying,
    exportRef,
    pnlContent,
    cardTheme,
    handleDownload,
    handleCopy,
    handleTwitterShare,
  };
}
